import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { getSessionWithRetry } from '../utils/authSessionUtils';

/**
 * AuthCallback — standalone route rendered at /auth/callback.
 *
 * PURPOSE: When Supabase redirects back from Google (or any future OAuth
 * provider), the URL contains a PKCE ?code= parameter. Because
 * detectSessionInUrl is true globally in supabase.js, the JS client
 * automatically exchanges that code for a session when this component
 * mounts. This route waits for the session to appear, then branches:
 *
 *   player row exists → navigate('/')          (existing or just-linked user)
 *   no player row     → navigate('/auth/complete-profile')  (new Google user)
 *
 * IMPORTANT: This component does NOT call ensurePlayerRecord or create any
 * database rows. All writes happen on /auth/complete-profile. This keeps
 * the callback read-only and avoids races with the global auth listener.
 *
 * SECURITY: Do not log full URLs (contains ?code=). No PII in error UI.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const handleCallback = async () => {
      try {
        // Exchange PKCE code explicitly when present (more reliable than relying
        // solely on detectSessionInUrl racing App mount).
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            console.error('[AUTH] oauth_code_exchange_error', exchangeError);
            setError(
              'Sign-in could not be completed. Please try again from the login page.'
            );
            return;
          }
        }

        const { session } = await getSessionWithRetry(8, 150);

        if (cancelled) return;

        if (!session) {
          setError(
            'Sign-in could not be completed. The link may have expired or ' +
            'already been used. Please try signing in again.'
          );
          return;
        }

        // Check whether a players row already exists for this auth user.
        const { data: playerRows, error: playerError } = await supabase
          .from('players')
          .select('id')
          .eq('auth_id', session.user.id)
          .limit(1);

        if (cancelled) return;

        if (playerError) {
          console.error('[AUTH] oauth_callback_player_lookup_error', playerError);
          setError('Sign-in succeeded but we could not load your profile. Please try again.');
          return;
        }

        if (playerRows && playerRows.length > 0) {
          // Existing user — route home; initializeAuth re-runs on pathname change
          // and resolveSessionForApp hydrates currentUser (init + listener only).
          navigate('/', { replace: true });
        } else {
          // New Google user — no player row yet. Send to profile completion.
          navigate('/auth/complete-profile', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[AUTH] oauth_callback_error', err);
          setError('An unexpected error occurred. Please try signing in again.');
        }
      }
    };

    handleCallback();
    return () => { cancelled = true; };
  }, [navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-sm">
          <h2 className="mb-2 text-lg font-semibold text-text-primary">Sign-in failed</h2>
          <p className="mb-6 text-sm text-text-muted">{error}</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
          >
            Back to sign-in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-text-muted">Completing sign-in…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
